/**
 * Session state for the whole app: who am I, am I loading, login/logout.
 * Subscribes to the http client's session-expired signal so a dead
 * refresh token anywhere routes the user back to /login.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import * as authApi from "../api/auth";
import { getTokens, onSessionExpired } from "../api/http";
import type { Me } from "../api/types";

interface AuthValue {
  user: Me | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<authApi.LoginResult>;
  verify2fa: (preAuthToken: string, code: string) => Promise<void>;
  signup: (email: string, password: string, fullName: string) => Promise<void>;
  /** Re-hydrate the current user from /me (e.g. after onboarding flips
   *  `onboarded` true, so guards re-evaluate). */
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  isSuperAdmin: boolean;
  isTenantAdmin: boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  // Boot: if a token exists, resolve the session; otherwise done.
  useEffect(() => {
    let cancelled = false;
    const { access } = getTokens();
    if (!access) {
      setLoading(false);
      return;
    }
    authApi
      .fetchMe()
      .then((me) => {
        if (!cancelled) setUser(me);
      })
      .catch(() => {
        // Expired/invalid session — http.ts already cleared tokens.
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Any API call discovering a dead session logs us out in the UI.
  useEffect(() => onSessionExpired(() => setUser(null)), []);

  const login = useCallback(async (email: string, password: string) => {
    const result = await authApi.login(email, password);
    // 2FA-enabled accounts have no session yet — the login page collects the
    // code and calls verify2fa. Only hydrate the user when we truly have one.
    if (!result.requires_2fa) setUser(await authApi.fetchMe());
    return result;
  }, []);

  const verify2fa = useCallback(async (preAuthToken: string, code: string) => {
    await authApi.verify2fa(preAuthToken, code);
    setUser(await authApi.fetchMe());
  }, []);

  const signup = useCallback(
    async (email: string, password: string, fullName: string) => {
      await authApi.signup(email, password, fullName);
      setUser(await authApi.fetchMe());
    },
    [],
  );

  const refresh = useCallback(async () => {
    setUser(await authApi.fetchMe());
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      login,
      verify2fa,
      signup,
      refresh,
      logout,
      isSuperAdmin: user?.role === "super_admin",
      isTenantAdmin: user?.role === "tenant_admin",
    }),
    [user, loading, login, verify2fa, signup, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
